<template>
  <InputWrapper
    v-slot="{ inputId }"
    :input-id="id"
    :label="label"
    class="location-groups-search-filter"
    optional
  >
    <RygenMultiSelect
      :id="inputId"
      :model-value="selectedIds"
      :options="allKnownLocationGroups"
      :loading="isLoading"
      :placeholder="t('locationGroups.locationGroups', 2)"
      append-to="body"
      display="chip"
      option-label="name"
      option-value="naturalId"
      data-key="naturalId"
      filter
      show-clear
      truncate-labels
      @update:model-value="onUpdate"
    />
  </InputWrapper>
</template>

<script lang="ts" setup>
import { computed, onMounted, type PropType, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { OrderSearchCriteria, ShipmentSearchCriteria } from '@/model';
import { useLocationGroupStore } from '@/stores/LocationGroup';

import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenMultiSelect from '@rygen/rygen-ui/components/form/RygenMultiSelect.vue';

type StopType = 'origin' | 'destination';

const internalValue = defineModel({
  type: Object as PropType<OrderSearchCriteria | ShipmentSearchCriteria>,
  required: true,
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  stopType: {
    type: String as PropType<StopType>,
    required: true,
  },
});
const { id, stopType } = toRefs(props);
const { t } = useI18n();
const {
  locationGroups,
  visibleLocationGroups,
  allKnownLocationGroups,
  loadLocationGroups,
} = useLocationGroupStore();

const label = computed(() => {
  const stopLabel =
    stopType.value === 'origin' ? t('orders.origin') : t('orders.destination');
  return `${stopLabel} ${t('locationGroups.locationGroups', 2)}`;
});

const isLoading = computed(
  () => locationGroups.value.loading || visibleLocationGroups.value.loading,
);

const selectedIds = computed(() =>
  stopType.value === 'origin'
    ? (internalValue.value.originLocationGroupIds ?? [])
    : (internalValue.value.destinationLocationGroupIds ?? []),
);

const onUpdate = (naturalIds: string[] | null) => {
  if (stopType.value === 'origin') {
    internalValue.value.originLocationGroupIds = naturalIds ?? [];
  } else {
    internalValue.value.destinationLocationGroupIds = naturalIds ?? [];
  }
};

onMounted(() => {
  loadLocationGroups({ includeVisible: true });
});
</script>
